// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Shared route access feedback. Wait for current workspace access before rendering.
// Facility policy and staff seat problems route to help, without purchase prompts.

import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isFreeTierAccountPath } from "@/lib/freeTierAccess";
import { invalidateEffectiveAccess, useEffectiveAccess } from "@/hooks/useEffectiveAccess";
import { lockReasonFor, navItemForPath } from "@/lib/effectiveAccess";
import { saveUpgradeIntent } from "@/lib/subscription/upgradeIntent";
import { trackSubscriptionEvent } from "@/lib/analytics/subscriptionEvents";
import { comingSoonToastForPath } from "@/content/comingSoonContent";
import { LockedModule } from "@/components/LockedModule";

export function ModuleAccessGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const { access, loading: accessLoading, error: accessError } = useEffectiveAccess();

  const lock = useMemo(() => {
    if (authLoading || accessLoading || accessError) return null;
    const item = navItemForPath(location.pathname);
    if (!item) return null;
    const reason = lockReasonFor(access, profile?.membership_tier, item, {
      isPlatformAdmin: profile?.role === "admin",
    });
    return reason ? { reason, item } : null;
  }, [location.pathname, access, profile, authLoading, accessLoading, accessError]);

  const reason = lock?.reason ?? null;

  useEffect(() => {
    if (reason === "tier" && lock && !(access?.is_member && ["member", "viewer"].includes(access.role))) {
      // Remember where they were headed so the post-upgrade success page can
      // send them straight back. The lock renders in place (no redirect).
      saveUpgradeIntent(location.pathname, lock.item.requiredTier);
      trackSubscriptionEvent("upgrade_intent_captured", {
        path: location.pathname,
        requiredTier: lock.item.requiredTier,
      });
    } else if (reason === "facility") {
      toast.info("Locked by your facility", {
        description: "Your facility admin has limited access to this module.",
      });
      navigate("/getting-started", { replace: true });
    } else if (reason === "comingSoon") {
      toast.info("Coming Soon", {
        description: comingSoonToastForPath(location.pathname),
      });
      navigate("/getting-started", { replace: true });
    } else if (reason === "admin") {
      toast.info("Admins only", {
        description: "That area is limited to administrators.",
      });
      navigate("/getting-started", { replace: true });
    }
  }, [reason, lock, location.pathname, navigate, access]);

  if (authLoading || accessLoading) return <div className="p-8 text-sm text-muted-foreground" role="status">Checking workspace access…</div>;
  if (accessError && navItemForPath(location.pathname) && !isFreeTierAccountPath(location.pathname)) return <div role="alert" className="p-8 space-y-3"><p>We couldn’t check your workspace access.</p><button className="text-primary underline" onClick={invalidateEffectiveAccess}>Try again</button></div>;
  if (reason === "tier" && lock && access?.is_member && (access.role === "member" || access.role === "viewer")) return <div className="p-8 space-y-3"><h1 className="text-xl">Workspace access needed</h1><p>Your facility administrator needs to assign access for this work. You do not need to purchase a personal plan.</p><button className="text-primary underline" onClick={() => navigate('/getting-started')}>Check my next step</button></div>;
  if (reason === "tier" && lock) return <LockedModule item={lock.item} />;
  if (reason === "comingSoon" || reason === "admin" || reason === "facility") return null;
  return <>{children}</>;
}

export default ModuleAccessGuard;
